/**
 * An application's backups: the ones it has, one taken now, and the schedule.
 *
 * A snapshot is a copy of the whole application, so taking one and restoring
 * one are both jobs -- they arrive in the same job list a duplicated
 * application does, and nothing here waits for them.
 *
 * **Restoring does not overwrite.** The endpoint creates a *new* application
 * from the snapshot and leaves the original alone, which is worth saying in the
 * interface: "restore" reads like something that cannot be undone, and this one
 * can simply be deleted.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Snapshot } from '@/api/types'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { byteSize } from '@/shared/lib/bytes'
import { whenText } from '@/shared/lib/when'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import { Separator } from '@/shared/ui/separator'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  useCreateSnapshot,
  useDeleteSnapshot,
  useRestoreSnapshot,
  useSnapshotDestinations,
  useSnapshots,
} from '../queries'
import { ScheduleSection } from './schedule-section'

export function SnapshotsDialog({
  applicationId,
  applicationName,
  open,
  onOpenChange,
}: {
  applicationId: number
  applicationName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const snapshots = useSnapshots(applicationId)
  const { data: destinations } = useSnapshotDestinations()
  const create = useCreateSnapshot(applicationId)

  const [name, setName] = useState('')
  const [destination, setDestination] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore'
    snapshot: Snapshot
  } | null>(null)

  const choices = destinations ?? []

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[80vh] max-w-2xl flex-col">
          <DialogHeader>
            <DialogTitle>{t('snapshot.title')}</DialogTitle>
            <DialogDescription>
              {t('snapshot.description', { name: applicationName })}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3">
            <div className="grid flex-1 gap-1">
              <Label htmlFor="snapshot-name" className="text-xs text-muted-foreground">
                {t('snapshot.name')}
              </Label>
              <Input
                id="snapshot-name"
                value={name}
                placeholder={t('snapshot.namePlaceholder')}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            {choices.length > 1 && (
              <div className="grid gap-1">
                <Label
                  htmlFor="snapshot-destination"
                  className="text-xs text-muted-foreground"
                >
                  {t('snapshot.destination')}
                </Label>
                <NativeSelect
                  id="snapshot-destination"
                  value={destination ?? choices[0]?.type ?? ''}
                  onChange={(event) => setDestination(event.target.value)}
                >
                  {choices.map((option) => (
                    <option key={option.type} value={option.type}>
                      {option.name}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}

            <Button
              disabled={name.trim() === '' || create.isPending}
              onClick={() => {
                const chosen = destination ?? choices[0]?.type
                create.mutate(
                  {
                    name: name.trim(),
                    ...(chosen !== undefined && { destination: chosen }),
                  },
                  {
                    // Cleared here rather than optimistically: the snapshot does
                    // not exist until the job finishes, and an emptied box with
                    // nothing new in the list would read as the request being
                    // lost.
                    onSuccess: () => setName(''),
                  },
                )
              }}
            >
              {t('snapshot.take')}
            </Button>
          </div>
          {/* The job list in the shell is what reports progress; this only says
              where to look, because the dialog can be closed while it runs. */}
          {create.isSuccess && (
            <p className="text-xs text-muted-foreground">{t('snapshot.taking')}</p>
          )}

          <Separator />

          <ScheduleSection applicationId={applicationId} destinations={choices} />

          <Separator />

          <div className="min-h-0 flex-1 overflow-y-auto">
            {snapshots.isPending ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-2/3" />
              </div>
            ) : (snapshots.data ?? []).length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">{t('snapshot.none')}</p>
            ) : (
              <ul className="space-y-2">
                {(snapshots.data ?? []).map((snapshot) => (
                  <li
                    key={snapshot.id}
                    className="flex items-center justify-between gap-2 rounded border p-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm">{snapshot.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {whenText(snapshot.created_at)}
                        {/* Scheduled or asked for, which is the difference
                            between a backup and a bookmark. */}
                        {snapshot.schedule != null && <> · {t('snapshot.scheduled')}</>}
                        {snapshot.size != null && <> · {byteSize(snapshot.size)}</>}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirming({ kind: 'restore', snapshot })}
                      >
                        {t('snapshot.restore')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirming({ kind: 'delete', snapshot })}
                      >
                        {t('action.delete')}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {confirming !== null && (
        <Confirm
          applicationId={applicationId}
          confirming={confirming}
          onDone={() => setConfirming(null)}
        />
      )}
    </>
  )
}

function Confirm({
  applicationId,
  confirming,
  onDone,
}: {
  applicationId: number
  confirming: { kind: 'delete' | 'restore'; snapshot: Snapshot }
  onDone: () => void
}) {
  const { t } = useTranslation()
  const remove = useDeleteSnapshot(applicationId)
  const restore = useRestoreSnapshot()

  const { kind, snapshot } = confirming

  return (
    <ConfirmDialog
      open
      onOpenChange={onDone}
      title={t(kind === 'delete' ? 'snapshot.deleteTitle' : 'snapshot.restoreTitle', {
        name: snapshot.name,
      })}
      description={t(
        kind === 'delete' ? 'snapshot.deleteDescription' : 'snapshot.restoreDescription',
      )}
      confirmLabel={t(kind === 'delete' ? 'action.delete' : 'snapshot.restore')}
      destructive={kind === 'delete'}
      onConfirm={() => {
        if (kind === 'delete') remove.mutate(snapshot.id)
        else restore.mutate(snapshot.id)
        onDone()
      }}
    />
  )
}
